import * as React from 'react';
import { cn } from '@/core/lib/utils';
import {
  floatingInputVariants,
  floatingLabelVariants,
} from '@/shared/components/custom/form-field/FloatingLabelInput';
import type { VariantProps } from 'class-variance-authority';
import { AsyncCombobox } from './AsyncCombobox';
import type { AsyncComboboxProps, AsyncComboboxOption } from './AsyncCombobox.types';

interface FloatingLabelAsyncComboboxOwnProps extends VariantProps<typeof floatingInputVariants> {
  label: string;
  id: string;
  invalid?: boolean;
  className?: string;
}

export type FloatingLabelAsyncComboboxProps<T extends AsyncComboboxOption = AsyncComboboxOption> =
  FloatingLabelAsyncComboboxOwnProps &
    Omit<AsyncComboboxProps<T>, 'triggerClassName' | 'className'>;

export function FloatingLabelAsyncCombobox<T extends AsyncComboboxOption = AsyncComboboxOption>({
  label,
  id,
  invalid = false,
  variant,
  className,
  ...comboboxProps
}: FloatingLabelAsyncComboboxProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false);

  const isMulti = comboboxProps.mode === 'multi';
  const hasValue = isMulti
    ? (comboboxProps.value as string[]).length > 0
    : !!(comboboxProps as { value: string | null }).value;

  const isFloating = isOpen || hasValue;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    comboboxProps.onOpenChange?.(open);
  };

  return (
    <div className="relative w-full flex items-center">
      {/* Border wrapper — provides visual outline matching floatingInputVariants */}
      <div
        className={cn(
          floatingInputVariants({ variant }),
          'p-0 flex items-center w-full',
          invalid && 'outline-destructive ring-2 ring-destructive/20',
          className,
        )}
      >
        <AsyncCombobox
          {...(comboboxProps as AsyncComboboxProps<T>)}
          onOpenChange={handleOpenChange}
          triggerClassName="border-0 shadow-none outline-none ring-0 focus-visible:ring-0 bg-transparent w-full rounded-lg text-sm"
        />
      </div>

      {/* Label — positioned relative to outer div, floats above border on open/value */}
      <label
        htmlFor={id}
        className={cn(
          floatingLabelVariants({ variant, floating: isFloating }),
          'pointer-events-none',
        )}
      >
        {label}
      </label>
    </div>
  );
}
